import Button from "@components/buttons/Button"
import ImageThumbnails from "@components/ImageThumbnails"
import Review from "@components/Review"
import Stars from "@components/Stars"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import { body16MainStyles, smallcapsBold14Styles } from "@constants/typography"
import { getIn } from "@utils/index"
import React from "react"
import styled from "styled-components"

import { YotpoReview } from "../../../reviews/types"

const Container = styled.div`
  display: flex;
  flex-direction: row;
  padding: calc(1 / 12 * 100vw) 0;
`

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
` as any

const Col = styled.div`
  display: flex;
  flex-direction: column;
`

const Header = styled(Col)`
  justify-content: flex-start;
  width: calc(4 / 12 * 100vw);
`

const Ratings = styled(Stars)`
  margin-right: 8px;
`

const RatingsText = styled.div`
  ${smallcapsBold14Styles}
  color: ${colors.black};
`

const BlackButton = styled(Button)`
  width: 256px;
  margin: 0 0 24px;
`

const WhiteButton = styled.button`
  cursor: pointer;
  height: 48px;
  background: transparent;
  border: 1px solid ${colors.black};
  width: fit-content;
  padding: 4px 24px 0;
  ${smallcapsBold14Styles}

  &:focus {
    outline-color: ${colors.black};
  }
`

const Content = styled(Col)`
  width: calc(6 / 12 * 100vw);
`

const EmptyText = styled.div`
  ${body16MainStyles}
  color: ${colors.mediumDarkGray};
  margin-left: 56px;
`

const StyledReview = styled(Review)`
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e7e6e5;

  &:last-child {
    border-bottom: none;
  }

  @media ${breakpoints.laptop} {
    margin-bottom: 32px;
    padding-bottom: 32px;
  }
`

const ImageThumbnailsWrapper = styled.div`
  display: none;

  @media ${breakpoints.laptop} {
    display: initial;
    margin-top: 72px;
    padding-right: calc(0.5 / 12 * 100vw);
  }
`

interface ReviewsBlockProps {
  avgRating: number
  numReviews: number
  onClickCount: () => void
  onClickImage: (id) => void
  reviewImages: Array<{
    alt: string
    id: number
    image: string
    onClick?: () => void
    review: number
    thumbnail: string
  }>
  reviews: YotpoReview[]
  setShowReviews: (value: boolean) => void
  setWriteReview: (value: boolean) => void
}

const ReviewsBlock = ({
  avgRating,
  numReviews,
  onClickCount,
  onClickImage,
  reviewImages,
  reviews,
  setShowReviews,
  setWriteReview,
}: ReviewsBlockProps) => {
  if (reviews.length > 0) {
    return (
      <Container>
        <Header>
          <Col>
            <Row
              css={`
                margin-bottom: 74px;
              `}
            >
              <Ratings stars={Math.round(avgRating)} />
              <RatingsText>{`${avgRating.toFixed(1)}  / 5`}</RatingsText>
            </Row>
          </Col>
          <Col>
            {numReviews > 4 && (
              <BlackButton
                onClick={() => setShowReviews(true)}
                text={`All Reviews (${numReviews})`}
              />
            )}
            <WhiteButton onClick={() => setWriteReview(true)}>
              Write A Review
            </WhiteButton>
          </Col>
          {reviewImages.length > 0 && (
            <ImageThumbnailsWrapper>
              <ImageThumbnails
                images={reviewImages}
                imagesToShow={4}
                onClickCount={onClickCount}
              />
            </ImageThumbnailsWrapper>
          )}
        </Header>
        <Content>
          {reviews.slice(0, 4)?.map((review, index) => (
            <StyledReview
              data={review}
              key={getIn(review, "id", index)}
              onClickImage={onClickImage}
            />
          ))}
        </Content>
      </Container>
    )
  }

  return (
    <Container>
      <Header>
        <Col>
          <Row>
            <Ratings stars={0} />
          </Row>
        </Col>
      </Header>
      <Content>
        <Row>
          <WhiteButton onClick={() => setWriteReview(true)}>
            Write A Review
          </WhiteButton>
          <EmptyText>
            There are no reviews yet - Be the first to write one!
          </EmptyText>
        </Row>
      </Content>
    </Container>
  )
}

export default ReviewsBlock
